import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Calendar, Filter, Trophy, Clock, MapPin, Users, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const FixtureCard = ({ fixture }) => {
  // Detect home/away robustly. football-data.org sets venue to the stadium
  // name (e.g. "West Ham United FC") on every fixture — home OR away — so
  // we must trust `home_away` first. FotMob uses `venue: "Home" | "Away"`.
  const rawHomeAway = (fixture.home_away || fixture.venue || "").toString();
  const isHome = /^home$/i.test(rawHomeAway);
  const displayVenue = isHome ? "Home" : /^away$/i.test(rawHomeAway) ? "Away" : (fixture.venue || rawHomeAway || "Home");

  const homeTeam = isHome ? "West Ham" : fixture.opponent;
  const awayTeam = isHome ? fixture.opponent : "West Ham";

  let homeScore, awayScore;
  if (fixture.west_ham_score != null && fixture.opponent_score != null) {
    homeScore = isHome ? fixture.west_ham_score : fixture.opponent_score;
    awayScore = isHome ? fixture.opponent_score : fixture.west_ham_score;
  } else {
    homeScore = fixture.score_home;
    awayScore = fixture.score_away;
  }

  const result = fixture.result || "";
  const isWin = result === "W";
  const isLoss = result === "L";

  // Kickoff time display: many U21 / U18 fixtures ship with `date` set to
  // midnight (e.g. "2026-08-01T00:00:00") but the real kickoff is in the
  // separate `time` field. Admin-authored rows (incl. Bulk Paste) ship
  // `date` as a plain "YYYY-MM-DD" (no T) + `time` "HH:MM" — in that
  // case `new Date("2026-08-16")` is parsed as UTC-midnight and reads
  // as "01:00" once we convert to Europe/London in BST, which is the
  // "shows 01:00" bug the user hit on EFL Championship rows.
  // A date string has a real kickoff embedded ONLY if it contains a "T"
  // separator AND that time isn't midnight.
  const timeStrValid = typeof fixture.time === "string" && /^\d{1,2}:\d{2}$/.test(fixture.time);
  const dateHasTime =
    typeof fixture.date === "string"
    && fixture.date.includes("T")
    && !/T00:00(:00)?/.test(fixture.date);
  const displayKickoff = dateHasTime
    ? new Date(fixture.date).toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" })
    : (timeStrValid ? fixture.time : "TBD");

  const teamLabel = fixture.team_label || fixture.team || "";

  // Highlight helper — West Ham is always shown bold in claret; the other
  // side is shown in a lighter, non-bold slate colour.
  const rowClass = (isWestHam) =>
    isWestHam
      ? "text-sm font-bold text-[#7C2C3B]"
      : "text-sm font-medium text-slate-600";

  return (
    <div className="fixture-card rounded-lg hover:shadow-md transition-shadow" data-testid={`fixture-${fixture.id}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="badge-claret text-xs">{fixture.competition}</span>
        {teamLabel && teamLabel !== "men" && (
          <span className="text-xs bg-[#2BAEE5]/10 text-[#2BAEE5] px-2 py-0.5 rounded font-medium">
            {teamLabel === "men" ? "Men" : teamLabel === "women" ? "Women" : teamLabel === "u21" ? "U21" : teamLabel === "u18" ? "U18" : teamLabel}
          </span>
        )}
      </div>

      <div className="flex items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={rowClass(isHome)}>
              {homeTeam}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className={rowClass(!isHome)}>
              {awayTeam}
            </span>
          </div>
        </div>

        {fixture.is_completed ? (
          <div className={`text-center px-4 py-2 rounded-lg ${isWin ? "bg-green-50" : isLoss ? "bg-red-50" : "bg-slate-50"}`}>
            <div className="score-display text-2xl">
              <span className={isWin && isHome ? "text-green-600" : ""}>{homeScore}</span>
              <span className="text-slate-300 mx-1">-</span>
              <span className={isWin && !isHome ? "text-green-600" : ""}>{awayScore}</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">FT</div>
          </div>
        ) : (
          <div className="text-center px-4 py-2 bg-slate-50 rounded-lg">
            <div className="text-lg font-bold text-[#7C2C3B]" style={{ fontFamily: "Barlow Condensed" }}>
              {displayKickoff}
            </div>
            <div className="text-xs text-slate-500">
              {new Date(fixture.date).toLocaleDateString("en-GB", { timeZone: "Europe/London", day: "numeric", month: "short" })}
            </div>
          </div>
        )}
      </div>

      {fixture.scorers && fixture.scorers.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="flex flex-wrap gap-2">
            {fixture.scorers.map((scorer, idx) => (
              <span key={idx} className="text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded">
                {scorer.name} {scorer.minute}'
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {new Date(fixture.date).toLocaleDateString("en-GB", { timeZone: "Europe/London", weekday: "short", day: "numeric", month: "short", year: "numeric" })}
        </span>
        <span className="flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {displayVenue}
        </span>
      </div>
    </div>
  );
};

export default function FixturesPage() {
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState("all");
  const [selectedCompetition, setSelectedCompetition] = useState("all");
  const [competitions, setCompetitions] = useState([]);
  const [retryCount, setRetryCount] = useState(0);

  const teams = [
    { value: "men", label: "Men's First Team" },
    { value: "women", label: "Women's First Team" },
    { value: "u21", label: "Men's U21" },
    { value: "u18", label: "Men's U18" },
  ];

  useEffect(() => {
    fetchFixtures();
  }, [selectedTeam, selectedCompetition]);

  // Auto-retry when fixtures empty (uses state counter to guarantee re-renders)
  useEffect(() => {
    if (!loading && fixtures.length === 0 && retryCount < 8) {
      const timer = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        fetchFixtures();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [loading, fixtures, retryCount]);

  const fetchFixtures = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedTeam !== "all") params.append("team", selectedTeam);

      const response = await axios.get(`${API}/fixtures?${params.toString()}`);
      let data = response.data;

      // Extract unique competitions
      const comps = [...new Set(data.map(f => f.competition).filter(Boolean))].sort();
      setCompetitions(comps);

      // Apply competition filter
      if (selectedCompetition !== "all") {
        data = data.filter(f => f.competition === selectedCompetition);
      }

      setFixtures(data);
    } catch (error) {
      console.error("Failed to fetch fixtures:", error);
    } finally {
      setLoading(false);
    }
  };

  const upcomingFixtures = fixtures
    .filter(f => !f.is_completed)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const completedFixtures = fixtures
    .filter(f => f.is_completed)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="fixtures-page">
      <SEO title="Fixtures & Results" path="/fixtures" description="West Ham United fixtures and results for all teams. Men's, Women's, U21 and U18 schedules across the EFL Championship, WSL, FA Cup and more." />
      <Navbar />

      <div className="bg-[#7C2C3B] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: "Barlow Condensed" }}>
            FIXTURES & RESULTS
          </h1>
          <p className="text-white/80">Complete fixture list for all West Ham teams including cup competitions</p>
        </div>
      </div>

      <div className="bg-white border-b border-slate-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filters:</span>
            </div>

            <Select value={selectedTeam} onValueChange={(v) => { setSelectedTeam(v); setSelectedCompetition("all"); }}>
              <SelectTrigger className="w-48" data-testid="filter-team">
                <SelectValue placeholder="All Teams" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                {teams.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedCompetition} onValueChange={setSelectedCompetition}>
              <SelectTrigger className="w-52" data-testid="filter-competition">
                <SelectValue placeholder="All Competitions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Competitions</SelectItem>
                {competitions.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : fixtures.length === 0 && retryCount < 8 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Loader2 className="w-8 h-8 text-[#7C2C3B] animate-spin mb-4" />
            <p className="text-slate-600 font-medium">Loading fixtures...</p>
            <p className="text-slate-400 text-sm mt-1">Fixture data is being compiled. This page will update automatically.</p>
          </div>
        ) : fixtures.length === 0 ? (
          <div className="empty-state">
            <Calendar className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-xl font-bold text-slate-700 mb-2" style={{ fontFamily: "Barlow Condensed" }}>NO FIXTURES FOUND</h3>
            <p className="text-slate-500">No fixtures match your current filters.</p>
          </div>
        ) : (
          <Tabs defaultValue="upcoming" className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="upcoming" data-testid="tab-upcoming">
                Upcoming ({upcomingFixtures.length})
              </TabsTrigger>
              <TabsTrigger value="results" data-testid="tab-results">
                Results ({completedFixtures.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upcoming">
              {upcomingFixtures.length === 0 ? (
                <div className="text-center py-12 text-slate-500">No upcoming fixtures scheduled</div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {upcomingFixtures.map(fixture => (
                    <FixtureCard key={fixture.id} fixture={fixture} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="results">
              {completedFixtures.length === 0 ? (
                <div className="text-center py-12 text-slate-500">No results to display</div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {completedFixtures.map(fixture => (
                    <FixtureCard key={fixture.id} fixture={fixture} />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      <Footer />
    </div>
  );
}
